type SummaryTranslate = (key: string) => string;

function interpolate(template: string, value: string): string {
  return template.replace('{0}', value);
}

/** Best-effort parse of generated DDL into operator-friendly bullet points. */
export function buildMigrationSummaryBullets(
  generatedSql: string | null | undefined,
  changeDescription: string | null | undefined,
  translate: SummaryTranslate
): string[] {
  const sql = (generatedSql ?? '').trim();
  const description = (changeDescription ?? '').trim();
  if (!sql) {
    return description ? [description] : [translate('dynamic.migration.summary.fallback')];
  }

  const bullets: string[] = [];
  const statements = sql
    .split(/;\s*(?=(?:[^']*'[^']*')*[^']*$)/)
    .map(item => item.trim())
    .filter(Boolean);

  for (const statement of statements) {
    const upper = statement.toUpperCase();

    const createTableMatch = /CREATE\s+TABLE\s+(?:\[?([\w]+)\]?\.?\[?([\w]+)\]?|(\w+))/i.exec(statement);
    if (upper.startsWith('CREATE TABLE') && createTableMatch) {
      const tableName = createTableMatch[2] ?? createTableMatch[3] ?? createTableMatch[1] ?? '';
      bullets.push(interpolate(translate('dynamic.migration.summary.createTable'), tableName));
      continue;
    }

    if (/INSERT\s+INTO[\s\S]+SELECT/i.test(upper)) {
      bullets.push(translate('dynamic.migration.summary.copyData'));
      continue;
    }

    const indexMatch = /CREATE\s+(UNIQUE\s+)?INDEX\s+(?:\[?([\w]+)\]?|(\w+))/i.exec(statement);
    if (indexMatch) {
      const indexName = indexMatch[2] ?? indexMatch[3] ?? '';
      bullets.push(interpolate(translate('dynamic.migration.summary.addIndex'), indexName));
      continue;
    }

    if (/FOREIGN\s+KEY/i.test(upper) || /ADD\s+CONSTRAINT[\s\S]+FOREIGN\s+KEY/i.test(upper)) {
      bullets.push(translate('dynamic.migration.summary.addForeignKey'));
      continue;
    }

    const dropTableMatch = /DROP\s+TABLE\s+(?:\[?([\w]+)\]?\.?\[?([\w]+)\]?|(\w+))/i.exec(statement);
    if (upper.startsWith('DROP TABLE') && dropTableMatch) {
      const tableName = dropTableMatch[2] ?? dropTableMatch[3] ?? dropTableMatch[1] ?? '';
      bullets.push(interpolate(translate('dynamic.migration.summary.dropTable'), tableName));
      continue;
    }

    if (upper.startsWith('ALTER TABLE')) {
      bullets.push(translate('dynamic.migration.summary.alterTable'));
    }
  }

  if (!bullets.length) {
    return description ? [description] : [translate('dynamic.migration.summary.fallback')];
  }

  if (description) {
    return [description, ...bullets];
  }

  return bullets;
}
